import './StandingsStandard.css';
import { OfficeBackButton } from '../shared/OfficeBackButton';
import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Info } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { StandingsTable, type StandingsRow } from './StandingsTable';
import { LiveStandingsTable } from './LiveStandingsTable';
import { StandingsWindowNav } from './StandingsWindowNav';
import { getStandings, getDailyStandings, getLiveStandings, getStandingsWindow, getDailyStandingsWindow, getFollowedStandings, getFollowedDailyStandings, getMonthlyStandings, getMonthlyStandingsWindow, getMonthlyStandingsMonths, type PointSettings, type LiveStandingsRow, type MonthlyStandingsMonth } from '../../lib/api/standings';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { useMaintenanceMode } from '../../lib/hooks/useMaintenanceMode';
import toast from 'react-hot-toast';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { useEmbedStore } from '../../lib/embedStore';
import { useTenantStore } from '../../lib/tenantStore';
import {
  computeUserPosition,
  formatMyPosition,
  windowOffsetsForStart,
  windowStartAround,
  STANDINGS_WINDOW_RADIUS,
  STANDINGS_WINDOW_SIZE,
  STANDINGS_WINDOW_STEP
} from '../../lib/standingsPosition';
import { getFollowedPoolerIdSet, supabase } from '../../lib/supabase';
import './StandingsEmbed.css';

type ActiveTab = 'total' | 'daily' | 'monthly' | 'live';
type WindowNavTab = 'total' | 'monthly';
// Fenêtre de classement navigable (embed Arsenal) : rang du pooler courant
// (référence des RPCs "window") et premier rang affiché (null = autour de moi).
type WindowNavState = { myRank: number | null; start: number | null };
type StandingsMode = 'all' | 'followed';
type BpmOnboardingLocationState = {
  bpmOnboardingTarget?: unknown;
};

interface StandingsProps {
  mode?: StandingsMode;
}

const defaultPointSettings: PointSettings = {
  pts_goal_att: 1,
  pts_ass_att: 1,
  pts_goal_def: 2,
  pts_ass_def: 1,
  pts_goal_g: 5,
  pts_win_g: 2,
  pts_shu_g: 3,
  pts_otlosse_g: 1
};

const FIRST_MONTHLY_STANDINGS_MONTH: MonthlyStandingsMonth = { year: 2026, month: 10 };

function isMonthlyStandingsSelectable(m: MonthlyStandingsMonth) {
  if (m.year !== FIRST_MONTHLY_STANDINGS_MONTH.year) return m.year > FIRST_MONTHLY_STANDINGS_MONTH.year;
  return m.month >= FIRST_MONTHLY_STANDINGS_MONTH.month;
}

const ARSENAL_FONTS_ID = 'arsenal-sports-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

function getStandingPoints(row: StandingsRow) {
  return (
    (Number(row.totgoals) || 0) +
    (Number(row.totassists) || 0) +
    (Number(row.totgoals_goalie) || 0) +
    (Number(row.totassists_goalie) || 0) +
    (Number(row.totwins_goalie) || 0) +
    (Number(row.totshutout_goalie) || 0) +
    (Number(row.tototlosses_goalie) || 0)
  );
}

function findUserRank(rows: StandingsRow[], userId: number | null) {
  if (userId === null) return null;
  const row = rows.find((r) => Number(r.user_id) === userId);
  return typeof row?.rank === 'number' ? row.rank : null;
}

function withGlobalRanks(rows: StandingsRow[]) {
  const hasRank = rows.some((row) => typeof row.rank === 'number');
  if (hasRank) return rows.slice();

  return rows
    .slice()
    .sort((a, b) => {
      const pointsDiff = getStandingPoints(b) - getStandingPoints(a);
      if (pointsDiff !== 0) return pointsDiff;
      return Number(a.user_id) - Number(b.user_id);
    })
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

// Le pooler courant est toujours conservé pour qu'il puisse se comparer à ses suivis.
function filterFollowedStandings(rows: StandingsRow[], followedUserIds: Set<number>, currentUserId: number | null) {
  if (followedUserIds.size === 0) return [];
  return withGlobalRanks(rows).filter(
    (row) => followedUserIds.has(Number(row.user_id)) || Number(row.user_id) === currentUserId
  );
}

export function Standings({ mode = 'all' }: StandingsProps) {
  // Tracker cette page
  usePageTracking(mode === 'followed' ? 'followedpoolers' : 'standing');
  const isFollowedMode = mode === 'followed';

  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const tenantId = useTenantStore((state) => state.tenantId);
  const isTslhEmbed = isEmbed && tenantId === 'tslh';
  const isArsenalEmbed = isEmbed && !isTslhEmbed;
  useArsenalFonts(isEmbed);

  const { isBuildPhase, isStandardFlexible } = useLeagueMode();
  const isBaseStandardUi = useBaseStandardUi(isStandardFlexible);

  const [pointSettings, setPointSettings] = useState<PointSettings>(defaultPointSettings);
  const [showScoringSystem, setShowScoringSystem] = useState(false);
  const { isMaintenance: inMaintenanceMode } = useMaintenanceMode();
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const setSelectedLeague = useLeagueStore((state) => state.setSelectedLeague);
  const currentLeagueId = useUserStore((state) => state.currentLeagueId);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const [standings, setStandings] = useState<StandingsRow[]>([]);
  const [dailyStandings, setDailyStandings] = useState<StandingsRow[]>([]);
  const [liveStandings, setLiveStandings] = useState<LiveStandingsRow[]>([]);
  const [followedUserIds, setFollowedUserIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [poolStartDate, setPoolStartDate] = useState<string | null>(null);
  // Classement mensuel : mois disponibles + mois sélectionné + données + états
  const [availableMonths, setAvailableMonths] = useState<MonthlyStandingsMonth[]>([]);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>('');
  const [monthlyStandings, setMonthlyStandings] = useState<StandingsRow[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);
  const [totalWindowNav, setTotalWindowNav] = useState<WindowNavState>({ myRank: null, start: null });
  const [monthlyWindowNav, setMonthlyWindowNav] = useState<WindowNavState>({ myRank: null, start: null });
  const [windowNavLoading, setWindowNavLoading] = useState(false);
  // Onglet par défaut : 'live' si en maintenance, sinon 'total'
  const [activeTab, setActiveTab] = useState<ActiveTab>('total');

  // Force live tab when maintenance is active
  useEffect(() => {
    if (inMaintenanceMode && !isFollowedMode) {
      setActiveTab('live');
    } else if (isFollowedMode && activeTab === 'live') {
      setActiveTab('total');
    }
  }, [activeTab, inMaintenanceMode, isFollowedMode]);
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [lastLiveUpdate, setLastLiveUpdate] = useState<Date | null>(null);
  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const liveErrorNotifiedRef = useRef(false);

  // Onboarding: ref + highlight when navigated from the BPM tour
  const pageRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const highlightElement = (el: HTMLElement) => {
    const originalBoxShadow = el.style.boxShadow;
    el.style.transition = 'box-shadow 0.3s, transform 0.3s';
    el.style.boxShadow = '0 0 0 6px rgba(245,166,35,0.55)';
    el.style.transform = 'translateY(-2px)';
    setTimeout(() => {
      el.style.boxShadow = originalBoxShadow;
      el.style.transform = 'translateY(0)';
    }, 2500);
  };

  useEffect(() => {
    const maybeState = (location.state as BpmOnboardingLocationState | null) ?? null;
    if (maybeState && typeof maybeState.bpmOnboardingTarget === 'number') {
      const idx = maybeState.bpmOnboardingTarget as number;
      // Tour card index 2 corresponds to "Classement"
      if (idx === 2) {
        const target = pageRef.current ?? document.querySelector('.card.shadow-lg');
        if (target) {
          (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlightElement(target as HTMLElement);
        }
      }
    }
  }, [location.key, location.state]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadData(currentLeagueId: number) {
      try {
        setLoading(true);
        const settingsResponse = await getLeagueSettings(currentLeagueId);

        const settingsData = settingsResponse.data;
        const resolvedSettings: PointSettings = {
          pts_goal_att: settingsData?.pts_goal_att ?? defaultPointSettings.pts_goal_att,
          pts_ass_att: settingsData?.pts_ass_att ?? defaultPointSettings.pts_ass_att,
          pts_goal_def: settingsData?.pts_goal_def ?? defaultPointSettings.pts_goal_def,
          pts_ass_def: settingsData?.pts_ass_def ?? defaultPointSettings.pts_ass_def,
          pts_goal_g: settingsData?.pts_goal_g ?? defaultPointSettings.pts_goal_g,
          pts_win_g: settingsData?.pts_win_g ?? defaultPointSettings.pts_win_g,
          pts_shu_g: settingsData?.pts_shu_g ?? defaultPointSettings.pts_shu_g,
          pts_otlosse_g: settingsData?.pts_otlosse_g ?? defaultPointSettings.pts_otlosse_g
        };

        if (!isMounted) return;

        const startDate = settingsData?.pool_start_date ?? null;
        setPoolStartDate(startDate);
        setPointSettings(resolvedSettings);

        if (inMaintenanceMode && !isFollowedMode) {
          setStandings([]);
          setDailyStandings([]);
          setLiveStandings([]);
          setLastLiveUpdate(null);
          setLiveError(null);
          liveErrorNotifiedRef.current = false;
          setActiveTab('live');
          return;
        }

        let loadedFollowedUserIds = new Set<number>();
        if (currentUserId) {
          const followedResponse = await getFollowedPoolerIdSet(currentLeagueId, Number(currentUserId));
          if (followedResponse.error) {
            console.warn('Impossible de charger les poolers suivis:', followedResponse.error);
          } else {
            loadedFollowedUserIds = followedResponse.data;
          }
        }

        if (!isMounted) return;

        setFollowedUserIds(loadedFollowedUserIds);

        if (isFollowedMode && loadedFollowedUserIds.size === 0) {
          setStandings([]);
          setDailyStandings([]);
          setLiveStandings([]);
          setLastLiveUpdate(null);
          setLiveError(null);
          return;
        }

        // TSLH embed doit afficher le classement complet pour permettre à
        // l'utilisateur de scroller vers le haut/bas de la grille. On garde
        // les RPCs "window" pour Arsenal / mode standard_flexible normal.
        const useWindow = !isFollowedMode && isStandardFlexible && currentUserId && !isTslhEmbed;

        async function loadTotalStandings() {
          if (useWindow) {
            const windowResp = await getStandingsWindow(currentLeagueId, Number(currentUserId), 5, 5);
            if (!windowResp.error) return windowResp;
            console.warn('getStandingsWindow failed, falling back to full standings:', windowResp.error);
          }
          return getStandings(currentLeagueId, startDate);
        }

        const [dailyResponse, totalResponse] = isFollowedMode
          ? await Promise.all([
              getFollowedDailyStandings(currentLeagueId, Number(currentUserId)),
              getFollowedStandings(currentLeagueId, Number(currentUserId))
            ])
          : await Promise.all([
              useWindow
                ? getDailyStandingsWindow(currentLeagueId, Number(currentUserId), 5, 5)
                : getDailyStandings(currentLeagueId),
              loadTotalStandings()
            ]);

        if (dailyResponse.error) throw dailyResponse.error;
        if (totalResponse.error) throw totalResponse.error;

        if (!isMounted) return;

        const viewerUserId = currentUserId ? Number(currentUserId) : null;

        const dailyRows = (dailyResponse.data || []) as StandingsRow[];
        setDailyStandings(isFollowedMode ? filterFollowedStandings(dailyRows, loadedFollowedUserIds, viewerUserId) : dailyRows);

        const totalRows = (totalResponse.data || []) as StandingsRow[];
        setStandings(isFollowedMode ? filterFollowedStandings(totalRows, loadedFollowedUserIds, viewerUserId) : totalRows);
        // Le fallback "classement complet" n'a pas de rang : la navigation reste alors masquée.
        setTotalWindowNav({ myRank: useWindow ? findUserRank(totalRows, viewerUserId) : null, start: null });
        setLiveStandings([]);
        setLastLiveUpdate(null);
        setLiveError(null);
        liveErrorNotifiedRef.current = false;
      } catch (error) {
        console.error('Error loading standings:', error);
        if (inMaintenanceMode && isStandardFlexible && isBuildPhase) {
          setStandings([]);
          setDailyStandings([]);
          setLiveStandings([]);
          setActiveTab('live');
          return;
        }
        toast.error(translate('failed.load.standings', language));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    async function resolveLeagueAndLoad() {
      const effectiveLeagueId = selectedLeague?.id ?? currentLeagueId;

      if (!effectiveLeagueId) {
        navigate('/select-league');
        return;
      }

      // En mode "nouveau flux", currentLeagueId peut être présent sans selectedLeague.
      // On resynchronise pour éviter les redirections incorrectes vers la sélection de ligue.
      if (!selectedLeague?.id && currentLeagueId) {
        try {
          const { data: leagueData, error } = await supabase
            .from('leagues')
            .select('league_id, name')
            .eq('league_id', currentLeagueId)
            .single();

          if (!error && leagueData) {
            setSelectedLeague({ id: leagueData.league_id, name: leagueData.name });
          }
        } catch (syncError) {
          console.warn('Impossible de resynchroniser selectedLeague:', syncError);
        }
      }

      await loadData(effectiveLeagueId);
    }

    resolveLeagueAndLoad();

    return () => {
      isMounted = false;
    };
  }, [selectedLeague?.id, currentLeagueId, navigate, language, setSelectedLeague, isStandardFlexible, currentUserId, inMaintenanceMode, isBuildPhase, isTslhEmbed, isFollowedMode]);

  const refreshLiveStandings = useCallback(async () => {
    if (isFollowedMode) return;

    const leagueId = selectedLeague?.id ?? currentLeagueId;
    if (!leagueId) {
      console.log('❌ No league selected');
      return;
    }

    if (inMaintenanceMode && isStandardFlexible && isBuildPhase) {
      setLiveStandings([]);
      setLastLiveUpdate(null);
      setLiveError(null);
      setIsLiveLoading(false);
      liveErrorNotifiedRef.current = false;
      return;
    }

    console.log('🔄 Refreshing live standings for league:', leagueId);
    setIsLiveLoading(true);
    setLiveError(null);

    try {
      // In standard_flexible mode, only compute and display the current user's club
      let userIds: number[] | undefined;
      if (isStandardFlexible && currentUserId) {
        userIds = [Number(currentUserId)];
      }

      const liveResponse = await getLiveStandings(leagueId, pointSettings, userIds);
      console.log('🔥 Live response received:', liveResponse);
      
      if (liveResponse.error) {
        console.error('❌ Live response has error:', liveResponse.error);
        throw liveResponse.error;
      }

      console.log('✅ Setting live standings:', liveResponse.data);
      liveErrorNotifiedRef.current = false;
      setLiveStandings(liveResponse.data || []);
      setLastLiveUpdate(new Date());
      setLiveError(null);
    } catch (error) {
      console.error('❌ Error loading live standings:', error);
      if (isMountedRef.current) {
        setLiveError('Impossible de charger les statistiques en direct.');
        if (!liveErrorNotifiedRef.current) {
          toast.error('Impossible de charger les statistiques en direct.');
          liveErrorNotifiedRef.current = true;
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsLiveLoading(false);
      }
    }
  }, [selectedLeague, currentLeagueId, pointSettings, isStandardFlexible, currentUserId, inMaintenanceMode, isBuildPhase, isFollowedMode]);

  useEffect(() => {
    if (activeTab !== 'live') {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
      return;
    }

    refreshLiveStandings();
    liveIntervalRef.current = setInterval(() => {
      refreshLiveStandings();
    }, 60000);

    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, [activeTab, refreshLiveStandings]);

  useEffect(() => {
    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, []);

  // --- Classement mensuel ---
  // Même logique de fenêtrage que le classement Total :
  // Arsenal/standard_flexible (hors TSLH embed) → fenêtre autour de l'utilisateur,
  // TSLH / dynasty → classement complet.
  const effectiveLeagueId = selectedLeague?.id ?? currentLeagueId;
  const useMonthlyWindow = isStandardFlexible && !!currentUserId && !isTslhEmbed && !isFollowedMode;

  // Charger la liste des mois réellement disponibles pour la ligue.
  useEffect(() => {
    let isMounted = true;
    if (!effectiveLeagueId) {
      setAvailableMonths([]);
      setSelectedMonthKey('');
      return;
    }

    getMonthlyStandingsMonths(effectiveLeagueId).then(({ data, error }) => {
      if (!isMounted) return;
      if (error || !data) {
        setAvailableMonths([]);
        setSelectedMonthKey('');
        return;
      }
      const selectable = data.filter(isMonthlyStandingsSelectable);
      setAvailableMonths(selectable);
      // Pré-sélectionner le mois le plus récent disponible (premier de la liste, triée DESC).
      setSelectedMonthKey((prev) => {
        if (prev && selectable.some((m) => `${m.year}-${m.month}` === prev)) return prev;
        return selectable.length > 0 ? `${selectable[0].year}-${selectable[0].month}` : '';
      });
    });

    return () => {
      isMounted = false;
    };
  }, [effectiveLeagueId]);

  // Charger les données mensuelles quand le mois sélectionné change.
  useEffect(() => {
    let isMounted = true;
    if (!effectiveLeagueId || !selectedMonthKey) {
      setMonthlyStandings([]);
      return;
    }

    const [yearStr, monthStr] = selectedMonthKey.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;

    async function loadMonthly(leagueId: number) {
      setMonthlyLoading(true);
      setMonthlyError(null);
      setMonthlyWindowNav({ myRank: null, start: null });
      try {
        let rows: StandingsRow[] = [];
        let myRank: number | null = null;
        if (useMonthlyWindow) {
          const windowResp = await getMonthlyStandingsWindow(leagueId, Number(currentUserId), year, month, 5, 5);
          if (!windowResp.error && Array.isArray(windowResp.data)) {
            rows = windowResp.data as StandingsRow[];
            myRank = findUserRank(rows, Number(currentUserId));
          } else {
            const fullResp = await getMonthlyStandings(leagueId, year, month);
            if (fullResp.error) throw fullResp.error;
            rows = (fullResp.data || []) as StandingsRow[];
          }
        } else {
          const fullResp = await getMonthlyStandings(leagueId, year, month);
          if (fullResp.error) throw fullResp.error;
          rows = (fullResp.data || []) as StandingsRow[];
        }

        if (!isMounted) return;
        setMonthlyStandings(rows);
        setMonthlyWindowNav({ myRank, start: null });
      } catch (error) {
        console.error('Error loading monthly standings:', error);
        if (isMounted) {
          setMonthlyError(translate('monthly.standings.error', language));
          setMonthlyStandings([]);
        }
      } finally {
        if (isMounted) setMonthlyLoading(false);
      }
    }

    loadMonthly(effectiveLeagueId);

    return () => {
      isMounted = false;
    };
  }, [effectiveLeagueId, selectedMonthKey, useMonthlyWindow, currentUserId, language]);

  // --- Navigation dans la fenêtre de classement (embed Arsenal, Total + Mensuel) ---
  // Les RPCs "window" sont centrées sur le pooler courant : on atteint n'importe
  // quelle plage de rangs en leur passant des offsets relatifs à son rang.
  async function moveStandingsWindow(tab: WindowNavTab, start: number | null) {
    const nav = tab === 'total' ? totalWindowNav : monthlyWindowNav;
    if (!effectiveLeagueId || !currentUserId || nav.myRank === null) return;

    const targetStart = start === null ? null : Math.max(1, start);
    const { before, after } =
      targetStart === null
        ? { before: STANDINGS_WINDOW_RADIUS, after: STANDINGS_WINDOW_RADIUS }
        : windowOffsetsForStart(nav.myRank, targetStart);

    setWindowNavLoading(true);
    try {
      let response;
      if (tab === 'total') {
        response = await getStandingsWindow(effectiveLeagueId, Number(currentUserId), before, after);
      } else {
        const [year, month] = selectedMonthKey.split('-').map(Number);
        response = await getMonthlyStandingsWindow(effectiveLeagueId, Number(currentUserId), year, month, before, after);
      }
      if (response.error) throw response.error;

      const rows = (response.data || []) as StandingsRow[];
      if (rows.length === 0) {
        toast.error(language === 'fr' ? 'Aucune équipe à cette position.' : 'No team at this position.');
        return;
      }

      if (tab === 'total') {
        setStandings(rows);
        setTotalWindowNav((prev) => ({ ...prev, start: targetStart }));
      } else {
        setMonthlyStandings(rows);
        setMonthlyWindowNav((prev) => ({ ...prev, start: targetStart }));
      }
    } catch (error) {
      console.error('Error moving standings window:', error);
      toast.error(translate('failed.load.standings', language));
    } finally {
      setWindowNavLoading(false);
    }
  }

  const renderWindowNav = (tab: WindowNavTab, rows: StandingsRow[]) => {
    const nav = tab === 'total' ? totalWindowNav : monthlyWindowNav;
    if (!isArsenalEmbed || nav.myRank === null || rows.length === 0) return null;

    const myRank = nav.myRank;
    const firstRank = rows[0].rank ?? 1;
    const lastRank = rows[rows.length - 1].rank ?? firstRank;
    // Si la dernière ligne reçue atteint la fin demandée, il reste potentiellement des équipes plus bas.
    const requestedEnd = nav.start === null ? myRank + STANDINGS_WINDOW_RADIUS : nav.start + STANDINGS_WINDOW_SIZE - 1;

    return (
      <StandingsWindowNav
        language={language}
        loading={windowNavLoading}
        canGoUp={firstRank > 1}
        canGoDown={lastRank >= requestedEnd}
        isShifted={nav.start !== null}
        onUp={() => moveStandingsWindow(tab, firstRank - STANDINGS_WINDOW_STEP)}
        onDown={() => moveStandingsWindow(tab, lastRank)}
        onJump={(position) => moveStandingsWindow(tab, position === myRank ? null : windowStartAround(position))}
        onReset={() => moveStandingsWindow(tab, null)}
      />
    );
  };

  const formatMonthLabel = (year: number, month: number) =>
    new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(new Date(Date.UTC(year, month - 1, 1)));

  const formattedLiveUpdate = lastLiveUpdate
    ? new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(lastLiveUpdate)
    : null;

  const currentStandings =
    activeTab === 'total'
      ? standings
      : activeTab === 'daily'
      ? dailyStandings
      : activeTab === 'monthly'
      ? monthlyStandings
      : liveStandings;

  const handleFollowChanged = (targetUserId: number, isFollowed: boolean) => {
    setFollowedUserIds((previous) => {
      const next = new Set(previous);
      if (isFollowed) {
        next.add(Number(targetUserId));
      } else {
        next.delete(Number(targetUserId));
      }
      return next;
    });

    if (isFollowedMode && !isFollowed) {
      setStandings((previous) => previous.filter((row) => Number(row.user_id) !== Number(targetUserId)));
      setDailyStandings((previous) => previous.filter((row) => Number(row.user_id) !== Number(targetUserId)));
    }
  };

  // La ligne du pooler courant est toujours présente : l'état vide dépend donc
  // du nombre de poolers suivis (ex. après avoir retiré le dernier suivi).
  const showFollowedEmptyState =
    isFollowedMode && activeTab !== 'live' && (followedUserIds.size === 0 || currentStandings.length === 0);

  // "Ma position : X sur Y" — affiché uniquement en mode embed TSLH, pour les
  // onglets Total et La veille. Le classement Live est intentionnellement exclu
  // (comportement, données et calcul ne doivent pas changer).
  const showMyPositionLine = isTslhEmbed && (activeTab === 'total' || activeTab === 'daily');

  const myPositionRows = activeTab === 'total' ? standings : dailyStandings;
  // En mode "Poolers suivis", la position est relative au groupe (moi + suivis,
  // déjà triés par rang global) plutôt que le rang global, pour rester
  // cohérente avec le total affiché.
  const followedGroupIndex = isFollowedMode
    ? myPositionRows.findIndex((row) => Number(row.user_id) === Number(currentUserId))
    : -1;
  const myPositionForTab =
    showMyPositionLine && currentUserId
      ? isFollowedMode
        ? {
            position: followedGroupIndex >= 0 ? followedGroupIndex + 1 : null,
            total: myPositionRows.length,
          }
        : computeUserPosition(myPositionRows, Number(currentUserId))
      : null;

  const myPositionText = myPositionForTab
    ? formatMyPosition(myPositionForTab.position, myPositionForTab.total, {
        my: translate('my.position', language),
        of: translate('of.total', language),
      })
    : null;

  // Scroll initial : sur TSLH embed uniquement, positionner la ligne de
  // l'utilisateur au centre du conteneur scrollable après le rendu. Cela
  // couvre naturellement les cas 1/N, 3/N, 60/N, 118/N, 120/N.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isTslhEmbed) return;
    if (activeTab === 'live') return;
    if (loading) return;
    if (!currentUserId) return;
    if (currentStandings.length === 0) return;

    // Laisse React finir de peindre le tableau, puis centre la ligne "is-me".
    const raf = requestAnimationFrame(() => {
      const container = scrollContainerRef.current?.querySelector<HTMLElement>('.standings-scroll');
      if (!container) return;
      const meRow = container.querySelector<HTMLTableRowElement>('tr.is-me');
      if (!meRow) return;

      const containerRect = container.getBoundingClientRect();
      const rowRect = meRow.getBoundingClientRect();
      // Offset actuel de la ligne relatif au haut scrollable du conteneur.
      const rowOffsetTop = rowRect.top - containerRect.top + container.scrollTop;
      const target = rowOffsetTop - container.clientHeight / 2 + meRow.clientHeight / 2;
      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      container.scrollTop = Math.max(0, Math.min(target, maxScroll));
    });

    return () => cancelAnimationFrame(raf);
  }, [isTslhEmbed, activeTab, loading, currentUserId, currentStandings]);

  const shouldShowIncompleteTeamLiveMessage =
    activeTab === 'live' &&
    inMaintenanceMode &&
    isStandardFlexible &&
    isBuildPhase &&
    !isLiveLoading &&
    !liveError &&
    liveStandings.length === 0;

  console.log('🎯 Current tab:', activeTab);
  console.log('📊 Current standings:', currentStandings);
  console.log('📈 Live standings state:', liveStandings);

  if (loading) {
    return (
      <div className={`card shadow-lg border-0${isEmbed ? ' standings-embed' : ''}`}>
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{translate('loading.standings', language)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card shadow-lg border-0${isEmbed ? ' standings-embed' : ''}`} ref={pageRef}>
      <div className={isBaseStandardUi && !isFollowedMode ? 'card-body p-3 p-md-5' : 'card-body p-5'}>
        <OfficeBackButton visible={isBaseStandardUi && !isFollowedMode} />
        <EmbedBackButton />
        {isFollowedMode && (
          <div className="mb-4">
            <h4 className="fw-bold mb-2">Poolers suivis</h4>
            <p className="text-muted mb-0">
              Suivez vos amis depuis le classement ou la page autres équipes pour les retrouver ici.
            </p>
          </div>
        )}
        {isStandardFlexible && isBuildPhase && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
            <span style={{ fontSize: '1.4rem', lineHeight: 1 }}></span>
            <div>
              <strong>
                {language === 'fr'
                  ? "Votre équipe n'est pas encore complétée !"
                  : 'Your team is not completed yet!'}
              </strong>
              <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                {language === 'fr'
                  ? "Vous devez compléter votre équipe dans la page « Bâtir mon équipe » pour commencer à accumuler des points."
                  : 'You must complete your team on the « Build My Team » page to start earning points.'}
              </div>
              <button
                className="btn btn-warning btn-sm mt-2"
                onClick={() => navigate('/build-my-team')}
              >
                {language === 'fr' ? 'Aller à Bâtir mon équipe' : 'Go to Build My Team'}
              </button>
            </div>
          </div>
        )}
        {poolStartDate && (
          <div className="alert alert-info d-flex align-items-center gap-2 mb-4" role="alert">
            <span style={{ fontSize: '1.2rem' }}>📅</span>
            <div>
              <strong>
                {language === 'fr' ? 'Date de début du pool' : 'Pool start date'}:
              </strong>{' '}
              {new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC'
              }).format(new Date(poolStartDate))}
              <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                {language === 'fr'
                  ? 'Les points au classement total sont comptabilisés à partir de cette date.'
                  : 'Total standings points are counted from this date onward.'}
              </div>
            </div>
          </div>
        )}
        <div className="card mb-4 border-0 bg-light">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center">
                <Info size={20} className="text-primary me-2" />
                <h5 className="mb-0">{translate('scoring.system', language)}</h5>
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                onClick={() => setShowScoringSystem((v) => !v)}
              >
                {showScoringSystem
                  ? (language === 'fr' ? 'Masquer' : 'Hide')
                  : (language === 'fr' ? 'Afficher' : 'Show')}
              </button>
            </div>

            {showScoringSystem && (
              <div className="row g-3 mt-1">
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{translate('forwards.scoring', language)}</h6>
                      <ul className="list-unstyled mb-0">
                        <li>{translate('forward.goal', language)}: {pointSettings.pts_goal_att} {translate('point', language)}</li>
                        <li>{translate('forward.assist', language)}: {pointSettings.pts_ass_att} {translate('point', language)}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{translate('defensemen.scoring', language)}</h6>
                      <ul className="list-unstyled mb-0">
                        <li>{translate('defense.goal', language)}: {pointSettings.pts_goal_def} {translate('points', language)}</li>
                        <li>{translate('defense.assist', language)}: {pointSettings.pts_ass_def} {translate('point', language)}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{translate('goalies.scoring', language)}</h6>
                      <ul className="list-unstyled mb-0">
                        <li>{translate('goalie.win', language)}: {pointSettings.pts_win_g} {translate('points', language)}</li>
                        <li>{translate('goalie.shutout', language)}: {pointSettings.pts_shu_g} {translate('points', language)}</li>
                        <li>{translate('goalie.goal', language)}: {pointSettings.pts_goal_g} {translate('points', language)}</li>
                        <li>{translate('stat.otLosses', language)}: {pointSettings.pts_otlosse_g} {translate('point', language)}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={isBaseStandardUi && !isFollowedMode ? 'standings-standard-tabs mb-4' : 'btn-group w-100 mb-4'}>
          <button
            className={`btn ${activeTab === 'total' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => !inMaintenanceMode && setActiveTab('total')}
            disabled={inMaintenanceMode}
            title={inMaintenanceMode ? "Disponible après 5h AM (Montréal)" : ""}
            style={inMaintenanceMode ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
          >
            {inMaintenanceMode && '🔒 '}Total
          </button>
          <button
            className={`btn ${activeTab === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => !inMaintenanceMode && setActiveTab('daily')}
            disabled={inMaintenanceMode}
            title={inMaintenanceMode ? "Disponible après 5h AM (Montréal)" : ""}
            style={inMaintenanceMode ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
          >
            {inMaintenanceMode && '🔒 '}De la veille
          </button>
          {!isFollowedMode && (
            <button
              className={`btn ${activeTab === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => !inMaintenanceMode && setActiveTab('monthly')}
              disabled={inMaintenanceMode}
              title={inMaintenanceMode ? "Disponible après 5h AM (Montréal)" : ""}
              style={inMaintenanceMode ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
            >
              {inMaintenanceMode && '🔒 '}{translate('monthly.standings', language)}
            </button>
          )}
          {!isFollowedMode && (
            <button
              className={`btn ${activeTab === 'live' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab('live')}
            >
              Stats live
            </button>
          )}
        </div>

        {inMaintenanceMode && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
            <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>🔧</span>
            <div>
              <strong>Maintenance en cours</strong>
              <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                L'application est en maintenance. Seul le <strong>classement live</strong> est disponible pendant cette période.
                Les autres sections seront accessibles après 5h00 AM (heure de Montréal).
              </div>
            </div>
          </div>
        )}

        {activeTab === 'live' && (
          <div className="mb-3">
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
              <div className="text-muted small">
                {formattedLiveUpdate
                  ? `Dernière mise à jour: ${formattedLiveUpdate}`
                  : 'Chargement des statistiques en direct...'}
              </div>
              <div className="d-flex align-items-center gap-2">
                {isLiveLoading && (
                  <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                )}
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={refreshLiveStandings}
                  disabled={isLiveLoading}
                >
                  Actualiser
                </button>
              </div>
            </div>
            {liveError && (
              <div className="alert alert-warning mt-3 mb-0" role="alert">
                {liveError}
              </div>
            )}
            <h6 className="mt-3 mb-5">{translate('standing_note.live', language)}</h6>
          </div>
        )}

        {activeTab === 'live' ? (
          shouldShowIncompleteTeamLiveMessage ? (
            <div className="alert alert-info mb-0" role="alert">
              <strong>
                {language === 'fr'
                  ? "Votre classement live n'est pas encore disponible."
                  : 'Your live standings are not available yet.'}
              </strong>
              <div className="mt-1">
                {language === 'fr'
                  ? "Vous devez terminer la construction de votre équipe avant de voir vos statistiques live."
                  : 'You must complete your team before seeing your live stats.'}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-3"
                onClick={() => navigate('/build-my-team')}
              >
                {language === 'fr' ? 'Aller à Bâtir mon équipe' : 'Go to Build My Team'}
              </button>
            </div>
          ) : (
            <LiveStandingsTable standings={liveStandings} />
          )
        ) : activeTab === 'monthly' ? (
          <div ref={scrollContainerRef}>
            {availableMonths.length > 0 && (
              <div className="mb-3" style={{ maxWidth: '260px' }}>
                <label htmlFor="monthly-standings-select" className="visually-hidden">
                  {translate('monthly.standings.select', language)}
                </label>
                <select
                  id="monthly-standings-select"
                  className="form-select"
                  value={selectedMonthKey}
                  onChange={(e) => setSelectedMonthKey(e.target.value)}
                  disabled={monthlyLoading || windowNavLoading}
                >
                  {availableMonths.map((m) => (
                    <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                      {formatMonthLabel(m.year, m.month)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="text-muted small mb-3">
              {translate('monthly.standings.start_note', language)}
            </div>

            {availableMonths.length === 0 ? (
              <div className="alert alert-info mb-0" role="status">
                {translate('monthly.standings.no_months', language)}
              </div>
            ) : monthlyLoading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{translate('loading.standings', language)}</span>
                </div>
              </div>
            ) : monthlyError ? (
              <div className="alert alert-warning mb-0" role="alert">
                {monthlyError}
              </div>
            ) : monthlyStandings.length === 0 ? (
              <div className="alert alert-info mb-0" role="status">
                {translate('monthly.standings.empty', language)}
              </div>
            ) : (
              <>
                {renderWindowNav('monthly', monthlyStandings)}
                <StandingsTable
                  standings={monthlyStandings}
                  language={language}
                  currentUserId={currentUserId ? Number(currentUserId) : null}
                  leagueId={selectedLeague?.id ?? currentLeagueId}
                  followedUserIds={followedUserIds}
                  showFollowButton
                  onFollowChanged={handleFollowChanged}
                  scrollable={isTslhEmbed}
                />
              </>
            )}
          </div>
        ) : (
          <>
            {myPositionText && (
              <div
                className="standings-my-position mb-3"
                data-testid="standings-my-position"
                role="status"
                aria-live="polite"
              >
                <strong>{myPositionText}</strong>
              </div>
            )}
            {showFollowedEmptyState ? (
              <div className="alert alert-info mb-0" role="status">
                <strong>Aucun pooler suivi</strong>
                <div className="mt-1">
                  Suivez vos amis depuis le classement ou la page autres équipes pour les retrouver ici.
                </div>
              </div>
            ) : (
              <div ref={scrollContainerRef}>
                {activeTab === 'total' && renderWindowNav('total', standings)}
                <StandingsTable
                  standings={currentStandings as StandingsRow[]}
                  language={language}
                  currentUserId={currentUserId ? Number(currentUserId) : null}
                  leagueId={selectedLeague?.id ?? currentLeagueId}
                  followedUserIds={followedUserIds}
                  showFollowButton
                  onFollowChanged={handleFollowChanged}
                  scrollable={isTslhEmbed}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
